import { Download, Expand, Upload } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ icon, label, onClick, disabled }: ToolbarButtonProps) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer disabled:opacity-50"
          aria-label={label}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface ArtifactToolbarActionsProps {
  /** When true, shows the download and export buttons. */
  featureFlagEnabled: boolean;
  /** Called when the download button is clicked (feature flag on). */
  onDownload?: () => void;
  /** Called when the generate-file button is clicked (feature flag off, fallback). */
  onGenerateFile?: () => void;
  /** Called when the export-to-workbench button is clicked. */
  onExport?: () => void;
  /** Called when the fullscreen button is clicked. */
  onFullscreen?: () => void;
  /** Whether to show the generate-file fallback button. */
  showGenerateFile?: boolean;
}

/**
 * Shared toolbar action buttons for artifact message components.
 * Renders download/generate, export, and fullscreen buttons with
 * consistent styling and layout.
 */
export function ArtifactToolbarActions({
  featureFlagEnabled,
  onDownload,
  onGenerateFile,
  onExport,
  onFullscreen,
  showGenerateFile = false,
}: ArtifactToolbarActionsProps) {
  return (
    <div className="ml-auto flex items-center">
      {featureFlagEnabled && onDownload ? (
        <ToolbarButton
          icon={<Download className="size-3.5" />}
          label="Download"
          onClick={onDownload}
        />
      ) : (
        showGenerateFile &&
        onGenerateFile && (
          <ToolbarButton
            icon={<Download className="size-3.5" />}
            label="Generate file from artifact"
            onClick={onGenerateFile}
          />
        )
      )}
      {featureFlagEnabled && onExport && (
        <ToolbarButton
          icon={<Upload className="size-3.5" />}
          label="Export to workbench"
          onClick={onExport}
        />
      )}
      {onFullscreen && (
        <ToolbarButton
          icon={<Expand className="size-3.5" />}
          label="View in full screen"
          onClick={onFullscreen}
        />
      )}
    </div>
  );
}
